import {
  ArrowLeft,
  ArrowRight,
  Building,
  Button,
  getRegionOptions,
  Globe,
  Input,
  Loader2,
  Mail,
  MapPin,
  PhoneInput,
  Search,
  Specialty,
  SpecialtySearchAutocomplete,
  User,
} from "@ews-admin/global-design-system";
import { Select } from "../components/atoms/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Steps } from "../components/molecules/Steps";
import { ToggleSwitch } from "../components/atoms/ToggleSwitch";
import {
  TRIAL_DURATION_OPTIONS,
  TrialDuration,
} from "./components/FacilityLicenceSection";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchSpecialtiesRequest,
  selectSpecialties,
} from "../store/reducers/specialtySlice";
import { useApiFormErrors } from "../hooks/useApiFormErrors";
import { useDebounce } from "../hooks/useDebounce";
import {
  FacilityFormData,
  ManagerFormData,
  PromotionFormData,
  facilityFormSchema,
  managerFormSchema,
  promotionFormSchema,
} from "../schemas/onboarding.schema";
import { facilitiesApi } from "../services/facilities.api";
import {
  dateInputToInstant,
  formatDateTime,
  instantToDateInput,
} from "../utils/date-format-utils";
import { formatMoney } from "../utils/money-utils";
import { licenceApi } from "../services/licence.api";
import { managerApi } from "../services/manager.api";
import { promotionApi } from "../services/promotion.api";
import {
  FacilityRequest,
  FacilityResponse,
  FacilityType,
  FacilityUpdate,
} from "../types/facility.definition";
import { DurationDiscount, LicencePlan } from "../types/licence.definition";
import {
  FALLBACK_DURATION_DISCOUNTS,
  applyDurationDiscount,
  findDiscountPercentage,
} from "../utils/duration-discount-utils";
import {
  DoctorLicencePlanType,
  FacilityContractDuration,
} from "../types/enums";
import { Manager, ManagerRequest } from "../types/manager.definition";
import {
  FREE_TRIAL_PROMOTION_CODE,
  PromotionCode,
} from "../types/promotion.definition";
import {
  AcceptedCountries,
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../types/user.definition";

// MED-1458: medecine360-api answers a rejected facility payload with a
// per-field list keyed by its own VM field names (HealthcareFacilityPayloadVM /
// HealthcareFacilityLocation). Translate those onto this wizard's form fields so
// each message lands on the input that caused it instead of in a generic modal.
const FACILITY_FIELD_BY_API_NAME: Record<string, keyof FacilityFormData> = {
  name: "name",
  website: "website",
  facilityType: "type",
  type: "type",
  contactEmail: "email",
  email: "email",
  phoneNumber: "phone",
  phone: "phone",
  "location.address": "address",
  address: "address",
  "location.city": "city",
  city: "city",
  "location.state": "state",
  state: "state",
  "location.zip": "zip",
  zip: "zip",
  "location.accessInformation": "accessInformation",
  accessInformation: "accessInformation",
};

type ContractDuration = "1_MONTH" | "1_YEAR" | "3_YEARS" | "5_YEARS";

const CONTRACT_DURATION_MAP: Record<ContractDuration, string> = {
  "1_MONTH": "ONE_MONTH",
  "1_YEAR": FacilityContractDuration.ONE_YEAR,
  "3_YEARS": FacilityContractDuration.THREE_YEARS,
  "5_YEARS": FacilityContractDuration.FIVE_YEARS,
};

interface OnboardingData {
  license: LicencePlan | null;
  doctorCount: number;
  contractDuration: ContractDuration | null;
  isTrial: boolean;
  trialDurationMonths: TrialDuration;
  selectedFacilityId: number | null;
  selectedManagerId: number | null;
  facility: {
    name: string;
    type: FacilityType;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    accessInformation?: string;
    website: string;
  };
  manager: {
    civility: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
  };
  // MED-1539: the promotion *period* that was picked, as it exists in
  // Licences -> Promotions. promotionKey is the licence_promotion id the assign
  // endpoint needs — the promotion code alone cannot say which period of it.
  promotion: {
    promotionCode: string;
    promotionKey: number;
    startDate: string | null;
    endDate: string | null;
    rebate: number | null;
  } | null;
}

const OnboardingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const storeSpecialties = useAppSelector(selectSpecialties);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    license: null,
    doctorCount: 1,
    // MED-1456: no duration is preselected — "Suivant" has to stay disabled
    // until the user picks one.
    contractDuration: null,
    isTrial: false,
    trialDurationMonths: 3,
    selectedFacilityId: null,
    selectedManagerId: null,
    facility: {
      name: "",
      type: "" as FacilityType,
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      accessInformation: "",
      website: "",
    },
    manager: {
      civility: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
    },
    promotion: null,
  });
  const [plans, setPlans] = useState<LicencePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [durationDiscounts, setDurationDiscounts] = useState<
    DurationDiscount[]
  >([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Id of the facility this wizard already created. Survives a failed attempt so
  // that retrying after a later step fails reuses it instead of creating another
  // establishment (MED-1417). A ref, not state: nothing renders from it and the
  // error handler below has to read it in the same tick it is written.
  const createdFacilityIdRef = useRef<number | null>(null);
  const [facilityAlreadyAssigned, setFacilityAlreadyAssigned] = useState(false);
  const [managerAlreadyHasFacility, setManagerAlreadyHasFacility] =
    useState(false);

  const [selectedSpecialties, setSelectedSpecialties] = useState<Specialty[]>(
    []
  );

  const specialtiesForWidget: Specialty[] = storeSpecialties.map(dto => ({
    id: String(dto.id),
    code: dto.code,
    label: dto.label ?? "",
  }));

  // Facility search state
  const [facilityMode, setFacilityMode] = useState<"search" | "form">("search");
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const [facilitySearchResults, setFacilitySearchResults] = useState<
    FacilityResponse[] | null
  >(null);
  const [facilitySearchLoading, setFacilitySearchLoading] = useState(false);
  const [facilitySearchError, setFacilitySearchError] = useState<string | null>(
    null
  );
  const debouncedFacilityQuery = useDebounce(facilitySearchQuery, 500);

  // Manager search state
  const [managerMode, setManagerMode] = useState<"search" | "form">("search");
  const [managerSearchQuery, setManagerSearchQuery] = useState("");
  const [managerSearchResults, setManagerSearchResults] = useState<
    Manager[] | null
  >(null);
  const [managerSearchLoading, setManagerSearchLoading] = useState(false);
  const [managerSearchError, setManagerSearchError] = useState<string | null>(
    null
  );
  const debouncedManagerQuery = useDebounce(managerSearchQuery, 500);

  const facilityForm = useForm<FacilityFormData>({
    resolver: zodResolver(facilityFormSchema(t)),
    defaultValues: onboardingData.facility,
  });

  // MED-1458: the field names the API rejects the facility payload with, mapped
  // onto this wizard's own inputs. The API sends its bean-validation message as
  // an unresolved key here (e.g. "medecin3d.invalid.field.pattern.website"), so
  // the wizard's own wording is preferred where it has one.
  const facilityApiErrors = useApiFormErrors<keyof FacilityFormData>({
    fields: FACILITY_FIELD_BY_API_NAME,
    fieldMessage: formField =>
      t(`onboarding.facility.${formField}Invalid`, { defaultValue: "" }) ||
      undefined,
  });

  const managerForm = useForm<ManagerFormData>({
    resolver: zodResolver(managerFormSchema(t)),
    defaultValues: onboardingData.manager,
  });

  // MED-1487: the promotion code is typed, not only picked, so the zod resolver
  // has to resolve it against the codes that exist. The resolver is built on the
  // first render, before the fetch below resolves, so it reads this ref at parse
  // time rather than closing over the state value.
  const applicablePromotionsRef = useRef<PromotionCode[]>([]);

  const promotionForm = useForm<PromotionFormData>({
    resolver: zodResolver(
      promotionFormSchema(t, () =>
        applicablePromotionsRef.current.map(p => p.promotionCode)
      )
    ),
    defaultValues: { promotionCode: "" },
  });

  const [promotions, setPromotions] = useState<PromotionCode[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(true);

  /**
   * MED-1487: the stored promotion matching a typed code, ignoring case and
   * padding — the promotion step takes the code as free text.
   *
   * Only promotions that can actually be applied are in `promotions` — see the
   * fetch below (MED-1539).
   */
  const resolvePromotionCode = (
    promotionCode?: string
  ): PromotionCode | undefined => {
    const typed = promotionCode?.trim();
    if (!typed) return undefined;
    return promotions.find(
      p => p.promotionCode.toLowerCase() === typed.toLowerCase()
    );
  };

  // MED-1539: the promotion dropdown printed translations.en, so a French UI
  // showed "Summer 2026 promotion" even though the French label exists.
  const promotionLabel = (promotion: PromotionCode): string => {
    const byLang: Record<string, string | undefined> = {
      fr: promotion.translations.fr,
      en: promotion.translations.en,
      ar: promotion.translations.ar,
    };
    const lang = (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];
    return (
      byLang[lang] ||
      promotion.translations.fr ||
      promotion.translations.en ||
      promotion.promotionCode
    );
  };

  const steps = [
    {
      id: "license",
      title: t("onboarding.steps.license.title"),
      description: t("onboarding.steps.license.description"),
    },
    {
      id: "facility",
      title: t("onboarding.steps.facility.title"),
      description: t("onboarding.steps.facility.description"),
    },
    {
      id: "manager",
      title: t("onboarding.steps.manager.title"),
      description: t("onboarding.steps.manager.description"),
    },
    {
      id: "promotion",
      title: t("onboarding.steps.promotion.title"),
      description: t("onboarding.steps.promotion.description"),
    },
    {
      id: "summary",
      title: t("onboarding.steps.summary.title"),
      description: t("onboarding.steps.summary.description"),
    },
  ];

  // Fetch specialties on mount and on language change
  useEffect(() => {
    dispatch(fetchSpecialtiesRequest({ lang: i18n.language }));
    const handleLanguageChange = (lang: string) => {
      dispatch(fetchSpecialtiesRequest({ lang }));
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [dispatch, i18n]);

  // Fetch plans from API. MED-1456: no plan is pre-selected — the STANDARD one
  // still carries the "Recommended" ribbon, but recommending it is not the same
  // as choosing it, and `isStep0Valid` has to be able to tell "nothing chosen
  // yet" from "chosen". `license` also stays null when this request fails, in
  // which case the banner below, not the cards, is what the user sees.
  useEffect(() => {
    licenceApi
      .getAvailablePlans()
      .then(setPlans)
      .catch(() => setError(t("onboarding.errorLoadingPlans")))
      .finally(() => setPlansLoading(false));
  }, [t]);

  // MED-1558: the duration rates come from the API, which bills with the same
  // values — the one-year rate is whatever the admin set on the Tarification
  // page. The hard-coded rates are only a last resort if the request fails.
  useEffect(() => {
    licenceApi
      .getDurationDiscounts()
      .then(setDurationDiscounts)
      .catch(() => setDurationDiscounts(FALLBACK_DURATION_DISCOUNTS));
  }, []);

  // Fetch active promotions for the promotion step.
  //
  // FREE_TRIAL is dropped here: granting a trial licence is already this
  // wizard's Step 1 toggle (isTrial/trialDurationMonths). Offering it again as
  // a promotion code would be a redundant, confusing second path to the same
  // outcome — this step is for rebate-type promotions only.
  //
  // MED-1539: so is a promotion with no validity period — there would be nothing
  // for the facility to be assigned to. This endpoint is built from the active
  // periods themselves, so every entry carries one; the filter only keeps the
  // step honest if that ever changes.
  useEffect(() => {
    promotionApi
      .getOnboardingPromotions()
      .then(fetched => {
        const applicable = fetched.filter(
          p =>
            p.promotionCode !== FREE_TRIAL_PROMOTION_CODE &&
            p.promotionId !== undefined
        );
        applicablePromotionsRef.current = applicable;
        setPromotions(applicable);
      })
      .catch(() => setPromotions([]))
      .finally(() => setPromotionsLoading(false));
  }, []);

  // Facility search effect
  useEffect(() => {
    if (debouncedFacilityQuery.trim().length < 2) {
      setFacilitySearchResults(null);
      setFacilitySearchError(null);
      return;
    }
    setFacilitySearchLoading(true);
    setFacilitySearchError(null);
    facilitiesApi
      .searchFacilities(debouncedFacilityQuery.trim())
      .then(results => {
        setFacilitySearchResults(results);
      })
      .catch(() => {
        setFacilitySearchError(t("common.error"));
        setFacilitySearchResults(null);
      })
      .finally(() => {
        setFacilitySearchLoading(false);
      });
  }, [debouncedFacilityQuery, t]);

  // Manager search effect
  useEffect(() => {
    if (debouncedManagerQuery.trim().length < 2) {
      setManagerSearchResults(null);
      setManagerSearchError(null);
      return;
    }
    setManagerSearchLoading(true);
    setManagerSearchError(null);
    managerApi
      .searchManagers(debouncedManagerQuery.trim())
      .then(results => {
        setManagerSearchResults(results);
      })
      .catch(() => {
        setManagerSearchError(t("common.error"));
        setManagerSearchResults(null);
      })
      .finally(() => {
        setManagerSearchLoading(false);
      });
  }, [debouncedManagerQuery, t]);

  // MED-1558: the licence is created without a payment billing mode (see
  // createLicence below), so the rate shown is the (duration, no mode) one.
  const getDiscountRate = (duration: ContractDuration | null): number =>
    duration
      ? findDiscountPercentage(
          durationDiscounts,
          CONTRACT_DURATION_MAP[duration]
        )
      : 0;

  const contractDurationOptions: {
    value: ContractDuration;
    discount: number;
  }[] = (["1_MONTH", "1_YEAR", "3_YEARS", "5_YEARS"] as const).map(value => ({
    value,
    discount: getDiscountRate(value),
  }));

  const calculateTotalPrice = (
    license: LicencePlan | null,
    doctorCount: number,
    duration: ContractDuration | null
  ): number => {
    if (!license) return 0;
    const pricePerDoctor = license.pricePerDoctor ?? 20000;
    const total = license.basePrice + doctorCount * pricePerDoctor;
    return applyDurationDiscount(total, getDiscountRate(duration)).amount;
  };

  // MED-1456: "Suivant" stays disabled until a plan and a duration have been
  // chosen. Nothing on this step is preselected any more, so this guard now
  // actually bites. A trial hides the contract-duration grid (MED-1219) and
  // offers the trial length instead, which always has a value — so outside a
  // trial the contract duration is the duration that has to be chosen.
  // MED-1539: medecine360-api takes the rebate off the invoice amount, rounded
  // to 2 decimals (PromotionPricingServiceImpl.applyActivePromotion) — mirror
  // that here rather than inventing a second rounding rule for the recap.
  const priceWithRebate = (price: number, rebate: number): number =>
    price - Math.round(((price * rebate) / 100) * 100) / 100;

  const isStep0Valid = (): boolean =>
    onboardingData.license !== null &&
    onboardingData.doctorCount > 0 &&
    (onboardingData.isTrial || onboardingData.contractDuration !== null);

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps([...completedSteps, currentStep + 1]);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (facilityMode === "search") {
        return;
      }
      // If an existing facility is selected, check it has no managers yet
      if (onboardingData.selectedFacilityId !== null) {
        setIsLoading(true);
        setError(null);
        try {
          const managers = await facilitiesApi.getFacilityManagers(
            onboardingData.selectedFacilityId
          );
          if (managers.length > 0) {
            setError(t("onboarding.error.facilityAlreadyAssigned"));
            setFacilityAlreadyAssigned(true);
            setIsLoading(false);
            return;
          }
          setFacilityAlreadyAssigned(false);
        } catch {
          // Non-blocking: if the check fails, allow proceeding
        } finally {
          setIsLoading(false);
        }
      }
      facilityForm.handleSubmit((data: FacilityFormData) => {
        const website = data.website;
        const normalizedWebsite = website
          ? !website.startsWith("http://") && !website.startsWith("https://")
            ? `https://${website}`
            : website
          : null;
        setOnboardingData(prev => ({
          ...prev,
          facility: { ...data, website: normalizedWebsite ?? "" },
        }));
        goToNextStep();
      })();
    } else if (currentStep === 2) {
      if (managerMode === "search") {
        return;
      }
      // If an existing manager was selected, verify they have no facility yet
      if (onboardingData.selectedManagerId !== null) {
        setIsLoading(true);
        setError(null);
        try {
          const mgr = await managerApi.getManagerById(
            onboardingData.selectedManagerId.toString()
          );
          if (mgr.healthcareFacility) {
            setError(t("onboarding.error.managerAlreadyHasFacility"));
            setManagerAlreadyHasFacility(true);
            setIsLoading(false);
            return;
          }
          setManagerAlreadyHasFacility(false);
        } catch {
          // non-blocking
        } finally {
          setIsLoading(false);
        }
      }
      // If creating a new manager, check for email duplicate
      if (onboardingData.selectedManagerId === null) {
        const emailValue = managerForm.getValues("email");
        if (emailValue) {
          setIsLoading(true);
          setError(null);
          try {
            const matches = await managerApi.searchManagers(emailValue);
            const exactMatch = matches.find(
              m => m.email.toLowerCase() === emailValue.toLowerCase()
            );
            if (exactMatch) {
              if (exactMatch.healthcareFacility) {
                setError(t("onboarding.error.managerAlreadyHasFacility"));
                setManagerAlreadyHasFacility(true);
                setIsLoading(false);
                return;
              }
              // Manager exists but has no facility yet — auto-assign
              setOnboardingData(prev => ({
                ...prev,
                selectedManagerId: exactMatch.id,
              }));
            }
          } catch {
            // Non-blocking: if the check fails, allow proceeding
          } finally {
            setIsLoading(false);
          }
        }
      }
      managerForm.handleSubmit((data: ManagerFormData) => {
        setOnboardingData(prev => ({ ...prev, manager: data }));
        goToNextStep();
      })();
    } else if (currentStep === 3) {
      promotionForm.handleSubmit((data: PromotionFormData) => {
        // MED-1487: carry the code as it is stored, not as it was typed — the
        // API looks it up by value. The schema has already refused anything
        // that does not resolve, so a non-empty entry always matches here.
        const resolved = resolvePromotionCode(data.promotionCode);
        setOnboardingData(prev => ({
          ...prev,
          // MED-1539: the period is the promotion's own — see the step below.
          promotion:
            resolved && resolved.promotionId !== undefined
              ? {
                  promotionCode: resolved.promotionCode,
                  promotionKey: resolved.promotionId,
                  startDate: resolved.startDate,
                  endDate: resolved.endDate,
                  rebate: resolved.rebate,
                }
              : null,
        }));
        goToNextStep();
      })();
    } else {
      goToNextStep();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setCompletedSteps(
        completedSteps.filter(step => step !== currentStep - 1)
      );
      pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCompleteOnboarding = async () => {
    setIsCompleting(true);
    setIsLoading(true);
    setError(null);

    let stage: "facility" | "manager" | "licence" | "promotion" = "facility";

    try {
      if (!onboardingData.license) {
        throw new Error("No license selected");
      }

      // Step 1: Create or update the facility
      const facilityLocation = {
        address: onboardingData.facility.address,
        city: onboardingData.facility.city,
        state: onboardingData.facility.state,
        zip: onboardingData.facility.zip,
        accessInformation:
          onboardingData.facility.accessInformation || undefined,
        country: AcceptedCountries.SENEGAL,
      };

      // An earlier attempt may already have created the facility; reuse it.
      const existingFacilityId =
        createdFacilityIdRef.current ?? onboardingData.selectedFacilityId;

      let facilityId: number;
      if (existingFacilityId !== null) {
        // Existing facility — update it
        const updateData: FacilityUpdate = {
          name: onboardingData.facility.name,
          facilityType: onboardingData.facility.type,
          contactEmail: onboardingData.facility.email,
          phoneNumber: onboardingData.facility.phone,
          website: onboardingData.facility.website || null,
          location: facilityLocation,
          specialtyIds: selectedSpecialties.map(s => Number(s.id)),
        };
        await facilitiesApi.updateFacility(existingFacilityId, updateData);
        facilityId = existingFacilityId;
      } else {
        // New facility — create it
        const facilityData: FacilityRequest = {
          name: onboardingData.facility.name,
          facilityType: onboardingData.facility.type,
          contactEmail: onboardingData.facility.email,
          phoneNumber: onboardingData.facility.phone,
          website: onboardingData.facility.website || null,
          location: facilityLocation,
          hours: [
            {
              monday: { open: "08:00", close: "18:00" },
              tuesday: { open: "08:00", close: "18:00" },
              wednesday: { open: "08:00", close: "18:00" },
              thursday: { open: "08:00", close: "18:00" },
              friday: { open: "08:00", close: "18:00" },
              saturday: { open: "09:00", close: "17:00" },
              sunday: { open: "10:00", close: "16:00" },
            },
          ],
          careTypeIds: [],
          specialtyIds: selectedSpecialties.map(s => Number(s.id)),
          insurance: [],
          isOpen: true,
          isPartner: false,
          healthcareFacilityServices: [],
          photoUrl: "",
          rating: 0,
          reviews: 0,
        };
        const createdFacility =
          await facilitiesApi.createFacility(facilityData);
        facilityId = createdFacility.id;
        createdFacilityIdRef.current = facilityId;
      }

      // Step 2: Create or assign the manager
      stage = "manager";
      if (onboardingData.selectedManagerId !== null) {
        await managerApi.assignManagerToFacility(
          facilityId,
          onboardingData.selectedManagerId
        );
      } else {
        const managerData: ManagerRequest = {
          photoUrl: "",
          email: onboardingData.manager.email,
          civility: onboardingData.manager.civility,
          roleName: RoleEnum.MANAGER,
          firstName: onboardingData.manager.firstName,
          phoneNumber: onboardingData.manager.phone,
          lastName: onboardingData.manager.lastName,
          preferredNotificationMethods: [NotificationPrefEnum.EMAIL],
          healthcareFacilityId: facilityId,
          ...(dateInputToInstant(onboardingData.manager.dateOfBirth)
            ? {
                dateOfBirth: dateInputToInstant(
                  onboardingData.manager.dateOfBirth
                ),
              }
            : {}),
        };
        await managerApi.createManager(managerData);
      }

      // Step 3: Create the licence
      stage = "licence";
      await licenceApi.createLicence(facilityId, {
        licenceType: onboardingData.license.type,
        numberOfDoctors: onboardingData.doctorCount,
        // LicenceRequest.contractDuration is @NotNull at the BFF. Outside a
        // trial the step-0 guard has already forced an explicit choice; a trial
        // never shows the contract-duration grid, so there is nothing for the
        // user to have chosen and ONE_YEAR is what the wizard has always sent
        // in that case (MED-1456).
        // MED-1558: no paymentBillingMode on purpose. A ONE_YEAR licence with
        // MONTHLY is also locked against any downgrade until its end date,
        // which a null mode is not, so the two are not billed alike; the
        // summary therefore quotes the (duration, no mode) rate.
        contractDuration:
          CONTRACT_DURATION_MAP[onboardingData.contractDuration ?? "1_YEAR"],
        healthcareFacilityId: facilityId,
        ...(onboardingData.isTrial
          ? { trialDurationMonths: onboardingData.trialDurationMonths }
          : {}),
      });

      // Step 4: Assign the facility to the promotion period that was selected on
      // the promotion step.
      //
      // MED-1539: this used to POST /onboarding/promotions/apply, which creates a
      // *new* validity period for the code from the dates the wizard sent and
      // attaches the facility to that one. The promotion the admin had actually
      // picked was therefore left with no facility: its page reported "0
      // establishment" and GET /admin/promotion/<id>/facilities came back empty.
      // The facility now joins the existing period through the same endpoint the
      // Promotions page's "Assign" action uses.
      if (onboardingData.promotion) {
        stage = "promotion";
        await promotionApi.assignPromotion({
          promotionKey: onboardingData.promotion.promotionKey,
          facilityId,
        });
      }

      // MED-1459: confirm the creation, and land on the facility that was just
      // created rather than on page 1 of an alphabetical list it is almost
      // never on.
      toast.success(t("onboarding.success.title"), {
        description: t("onboarding.success.subtitle"),
      });
      navigate(`/admin/facilities/${facilityId}`);
    } catch (error) {
      console.error("Onboarding failed at stage:", stage, error);

      // MED-1458: a rejected facility payload names the offending fields. Put
      // each message on its own input and send the user back to that step,
      // instead of leaving only "the data submitted is invalid". The mapping and
      // the "don't stack the generic modal on top" rule live in
      // useApiFormErrors, shared with every other form.
      if (stage === "facility") {
        const resolved = facilityApiErrors.handleError(error);
        const handled = Object.entries(resolved.fieldErrors) as [
          keyof FacilityFormData,
          string,
        ][];
        if (handled.length > 0) {
          for (const [formField, message] of handled) {
            facilityForm.setError(formField, { type: "server", message });
          }
          setError(
            [
              t("onboarding.error.facilityFieldsInvalid", {
                fields: handled
                  .map(([formField]) => t(`onboarding.facility.${formField}`))
                  .join(", "),
              }),
              // A field the wizard has no input for is named here rather than
              // dropped on the floor.
              resolved.formMessage,
            ]
              .filter(Boolean)
              .join(" ")
          );
          setCurrentStep(1);
          setFacilityMode("form");
          pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
          return;
        }
        if (resolved.formMessage) {
          // The server said what was wrong without naming a field the wizard
          // knows — its own words beat "the submitted data is invalid".
          setError(resolved.formMessage);
          return;
        }
      }

      let errorMessage: string;
      if (stage === "facility") {
        errorMessage = t("onboarding.error.facilityFailed");
      } else if (stage === "manager") {
        errorMessage = t("onboarding.error.managerFailed", {
          facilityId: createdFacilityIdRef.current,
        });
      } else if (stage === "promotion") {
        errorMessage = t("onboarding.error.promotionFailed", {
          facilityId: createdFacilityIdRef.current,
        });
      } else {
        errorMessage = t("onboarding.error.licenceFailed", {
          facilityId: createdFacilityIdRef.current,
        });
      }
      setError(errorMessage);
    } finally {
      setIsCompleting(false);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/admin/facilities");
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Doctor Count */}
            <div className="p-6 rounded-lg bg-gray-50">
              <h3 className="mb-1 text-lg font-semibold text-gray-900">
                {t("onboarding.steps.doctorCount.title")}
              </h3>
              <p className="mb-4 text-sm text-gray-600">
                {t("onboarding.steps.doctorCount.description")}
              </p>
              <div className="flex items-center gap-3">
                <button
                  className="flex items-center justify-center w-8 h-8 text-lg font-bold bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                  onClick={() =>
                    setOnboardingData({
                      ...onboardingData,
                      doctorCount: Math.max(1, onboardingData.doctorCount - 1),
                    })
                  }
                  disabled={onboardingData.doctorCount <= 1}
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={onboardingData.doctorCount}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1) {
                      setOnboardingData({
                        ...onboardingData,
                        doctorCount: val,
                      });
                    }
                  }}
                  className="w-20 p-2 text-lg font-semibold text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  className="flex items-center justify-center w-8 h-8 text-lg font-bold bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  onClick={() =>
                    setOnboardingData({
                      ...onboardingData,
                      doctorCount: onboardingData.doctorCount + 1,
                    })
                  }
                >
                  +
                </button>
                <span className="text-sm text-gray-500">
                  {/* MED-1543: a frozen plural read "1 médecins". */}
                  {t("onboarding.steps.doctorCount.doctors", {
                    count: onboardingData.doctorCount,
                  })}
                </span>
              </div>
            </div>

            {/* Free Trial Toggle */}
            <div className="flex items-center justify-between p-6 rounded-lg bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("facilityLicence.startTrialLabel")}
                </h3>
              </div>
              <ToggleSwitch
                checked={onboardingData.isTrial}
                onChange={checked =>
                  setOnboardingData({ ...onboardingData, isTrial: checked })
                }
                ariaLabel={t("facilityLicence.startTrialLabel")}
              />
            </div>

            {/* Trial Duration */}
            {onboardingData.isTrial && (
              <div className="p-6 rounded-lg bg-gray-50">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">
                  {t("onboarding.steps.trialDuration.title")}
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {TRIAL_DURATION_OPTIONS.map(option => {
                    const isSelected =
                      onboardingData.trialDurationMonths === option;
                    return (
                      <button
                        key={option}
                        className={`relative overflow-hidden p-4 rounded-lg border text-center transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                        onClick={() =>
                          setOnboardingData({
                            ...onboardingData,
                            trialDurationMonths: option,
                          })
                        }
                      >
                        <div className="font-semibold">
                          {t(
                            `onboarding.steps.trialDuration.${option}_MONTH${option > 1 ? "S" : ""}`
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contract Duration */}
            {!onboardingData.isTrial && (
              <div className="p-6 rounded-lg bg-gray-50">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">
                  {t("onboarding.steps.contractDuration.title")}
                </h3>
                <p className="mb-4 text-sm text-gray-600">
                  {t("onboarding.steps.contractDuration.description")}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {contractDurationOptions.map(option => {
                    // MED-1543: the recommendation is only a hint about where
                    // to start. Once a duration is chosen it must stop being
                    // drawn like a selection, or two options look chosen.
                    const isRecommendedDuration = option.value === "1_YEAR";
                    const isSelected =
                      onboardingData.contractDuration === option.value;
                    const highlightRecommendation =
                      isRecommendedDuration &&
                      onboardingData.contractDuration === null;
                    return (
                      <button
                        key={option.value}
                        className={`relative overflow-hidden p-4 rounded-lg border text-center transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                            : highlightRecommendation
                              ? "border-blue-300 bg-white hover:border-blue-400"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                        onClick={() =>
                          setOnboardingData({
                            ...onboardingData,
                            contractDuration: option.value,
                          })
                        }
                      >
                        {isRecommendedDuration && (
                          <div className="absolute top-7 right-[-34px] w-40 bg-blue-500 text-white text-xs font-bold text-center py-0.5 rotate-45 shadow">
                            {t("licenses.recommended")}
                          </div>
                        )}
                        <div className="font-semibold">
                          {t(
                            `onboarding.steps.contractDuration.${option.value}`
                          )}
                        </div>
                        {option.discount > 0 ? (
                          <div className="mt-1 text-xs font-medium text-green-600">
                            -{option.discount}%{" "}
                            {t("onboarding.steps.contractDuration.discount")}
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-gray-400">
                            {t("onboarding.steps.contractDuration.noDiscount")}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* MED-1456: nothing is preselected any more, so say what is
                    missing instead of leaving "Suivant" disabled with no
                    explanation.
                    MED-1543: in red, that read as a validation failure before
                    the user had touched anything. "Suivant" is disabled, so it
                    can never be rejected either — this is guidance about what
                    the step is waiting for, and it is styled as such. */}
                {onboardingData.contractDuration === null && (
                  <p className="mt-3 text-sm text-gray-500">
                    {t("onboarding.steps.contractDuration.required")}
                  </p>
                )}
              </div>
            )}

            {/* License Selection */}
            {plansLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {plans.map(plan => {
                  const isRecommended =
                    plan.type === DoctorLicencePlanType.STANDARD;
                  const isSelected = onboardingData.license?.type === plan.type;
                  const highlightRecommendation =
                    isRecommended && onboardingData.license === null;
                  const pricePerDoctor = plan.pricePerDoctor ?? 20000;
                  const discountRate = getDiscountRate(
                    onboardingData.contractDuration
                  );
                  const rawTotal =
                    plan.basePrice +
                    onboardingData.doctorCount * pricePerDoctor;
                  const totalPrice = calculateTotalPrice(
                    plan,
                    onboardingData.doctorCount,
                    onboardingData.contractDuration
                  );
                  const savings = rawTotal - totalPrice;
                  const planKey = plan.type
                    .toLowerCase()
                    .replace("standard", "professional");

                  return (
                    <div
                      key={plan.type}
                      className={`relative overflow-hidden border rounded-xl p-6 cursor-pointer transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 shadow-lg"
                          : highlightRecommendation
                            ? "border-blue-300 hover:border-blue-400 shadow-md"
                            : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() =>
                        setOnboardingData({ ...onboardingData, license: plan })
                      }
                    >
                      {isRecommended && (
                        <div className="absolute top-6 right-[-36px] w-40 bg-blue-500 text-white text-xs font-bold text-center py-1 rotate-45 shadow">
                          {t("licenses.recommended")}
                        </div>
                      )}
                      <h3
                        className={`text-lg font-semibold ${highlightRecommendation ? "text-blue-700" : "text-gray-900"}`}
                      >
                        {t(`licenses.plans.${planKey}`)}
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        {t(`licenses.plans.${planKey}Description`)}
                      </p>
                      <p className="mt-2 text-xs font-medium text-blue-600">
                        {!plan.maxSecretaries
                          ? t("licenses.noSecretaries")
                          : t("licenses.upToSecretaries", {
                              count: plan.maxSecretaries,
                            })}
                      </p>

                      {/* Features */}
                      {plan.features.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {plan.features.map(feature => {
                            const featureKey = `feature${
                              feature.charAt(0) +
                              feature
                                .slice(1)
                                .toLowerCase()
                                .replace(/_([a-z])/g, (_, c: string) =>
                                  c.toUpperCase()
                                )
                            }`;
                            return (
                              <li
                                key={feature}
                                className="flex items-start gap-1.5 text-xs text-gray-700"
                              >
                                <span className="mt-0.5 text-green-500 font-bold">
                                  ✓
                                </span>
                                <span>{t(`licenses.plans.${featureKey}`)}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      {/* Pricing */}
                      <div className="pt-3 mt-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <div className="flex justify-between">
                            <span>{t("licenses.basePrice")}</span>
                            <span className="font-medium">
                              {formatMoney(plan.basePrice ?? 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>{t("licenses.perDoctor")}</span>
                            <span className="font-medium">
                              ×{onboardingData.doctorCount}
                            </span>
                          </div>
                        </div>
                        <p className="mt-2 text-xl font-bold text-gray-900">
                          {formatMoney(totalPrice)}
                          <span className="text-sm font-normal text-gray-500">
                            /{t("licenses.month")}
                          </span>
                        </p>
                        {discountRate > 0 && savings > 0 && (
                          <div className="mt-0.5">
                            <div className="text-sm text-gray-500">
                              <span className="line-through">
                                {formatMoney(rawTotal)}
                              </span>
                              <span className="ml-2 font-medium text-green-600">
                                -{discountRate}%
                              </span>
                            </div>
                            <div className="mt-1 text-xs font-semibold text-green-600">
                              {t("licenses.save")} {formatMoney(savings)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* MED-1456: the STANDARD plan is recommended but no longer
                preselected, so name the missing choice. Only when there are
                cards to choose from: a failed getAvailablePlans leaves this
                list empty and is reported by the banner at the top of the
                page, not by a "pick a plan" that cannot be acted on. */}
            {!plansLoading &&
              plans.length > 0 &&
              onboardingData.license === null && (
                <p className="text-sm text-gray-500">
                  {t("onboarding.steps.license.required")}
                </p>
              )}
          </div>
        );

      case 1: {
        const {
          register: regFacility,
          control: facilityControl,
          formState: { errors: facilityErrors },
          setValue: setFacilityValue,
        } = facilityForm;
        const stateOptions = getRegionOptions(AcceptedCountries.SENEGAL);

        const facilityTypeLabel = (type: string): string => {
          const key = type.toLowerCase();
          return t(`onboarding.facility.types.${key}`, { defaultValue: type });
        };

        const handleSelectFacility = (f: FacilityResponse) => {
          setFacilityValue("name", f.name, { shouldValidate: false });
          setFacilityValue("type", f.facilityType, { shouldValidate: false });
          setFacilityValue("email", f.contactEmail, { shouldValidate: false });
          setFacilityValue("phone", f.phoneNumber, { shouldValidate: false });
          setFacilityValue("address", f.location?.address ?? "", {
            shouldValidate: false,
          });
          setFacilityValue("city", f.location?.city ?? "", {
            shouldValidate: false,
          });
          setFacilityValue("state", f.location?.state ?? "", {
            shouldValidate: false,
          });
          setFacilityValue("zip", f.location?.zip ?? "", {
            shouldValidate: false,
          });
          setFacilityValue("website", f.website ?? "", {
            shouldValidate: false,
          });
          setFacilityValue("accessInformation", "", { shouldValidate: false });
          // Picking a facility replaces anything a previous attempt created.
          createdFacilityIdRef.current = null;
          setOnboardingData(prev => ({
            ...prev,
            selectedFacilityId: f.id,
          }));
          setFacilityMode("form");
        };

        if (facilityMode === "search") {
          return (
            <div className="space-y-4">
              {/* Search input */}
              <div className="relative">
                <div className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={facilitySearchQuery}
                  onChange={e => setFacilitySearchQuery(e.target.value)}
                  placeholder={t("onboarding.facility.searchPlaceholder")}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Loading */}
              {facilitySearchLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t("onboarding.facility.searching")}</span>
                </div>
              )}

              {/* Search error */}
              {facilitySearchError && !facilitySearchLoading && (
                <p className="text-sm text-red-600">{facilitySearchError}</p>
              )}

              {/* Results */}
              {!facilitySearchLoading &&
                !facilitySearchError &&
                facilitySearchResults !== null && (
                  <div>
                    {facilitySearchResults.length === 0 ? (
                      <div className="p-4 space-y-3 text-center border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {t("onboarding.facility.noFacilityFound")}
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            facilityForm.reset();
                            createdFacilityIdRef.current = null;
                            setOnboardingData(prev => ({
                              ...prev,
                              selectedFacilityId: null,
                            }));
                            setFacilityMode("form");
                          }}
                        >
                          {t("onboarding.facility.addNewFacility")}
                        </Button>
                      </div>
                    ) : (
                      <ul className="overflow-hidden border border-gray-200 divide-y divide-gray-100 rounded-lg">
                        {facilitySearchResults.map(f => (
                          <li
                            key={f.id}
                            className="flex items-start gap-3 p-4 transition-colors cursor-pointer hover:bg-blue-50"
                            onClick={() => handleSelectFacility(f)}
                          >
                            <Building className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-gray-900 truncate">
                                  {f.name}
                                </p>
                                <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                  {facilityTypeLabel(f.facilityType)}
                                </span>
                              </div>
                              {f.location && (
                                <p className="mt-0.5 truncate text-sm text-gray-500">
                                  {[f.location.address, f.location.city]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

              {/* Hint when no search yet */}
              {!facilitySearchLoading &&
                facilitySearchResults === null &&
                !facilitySearchError && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      {t("onboarding.facility.searchHint")}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        facilityForm.reset();
                        createdFacilityIdRef.current = null;
                        setOnboardingData(prev => ({
                          ...prev,
                          selectedFacilityId: null,
                        }));
                        setFacilityMode("form");
                      }}
                    >
                      {t("onboarding.facility.addNewFacility")}
                    </Button>
                  </div>
                )}
            </div>
          );
        }

        // facilityMode === 'form'
        return (
          <div className="space-y-6">
            {/* Back to search */}
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
              onClick={() => {
                setFacilityMode("search");
                createdFacilityIdRef.current = null;
                setOnboardingData(prev => ({
                  ...prev,
                  selectedFacilityId: null,
                }));
                setFacilitySearchResults(null);
                setFacilitySearchQuery("");
                setFacilityAlreadyAssigned(false);
              }}
            >
              {t("onboarding.facility.backToSearch")}
            </button>

            {/* Existing facility banner */}
            {onboardingData.selectedFacilityId !== null && (
              <div className="px-4 py-3 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50">
                {t("onboarding.facility.existingFacilityBanner")} (ID:{" "}
                {onboardingData.selectedFacilityId})
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                type="text"
                label={t("onboarding.facility.name")}
                required
                placeholder={t("onboarding.facility.namePlaceholder")}
                leftIcon={<Building className="w-4 h-4" />}
                error={facilityErrors.name?.message}
                {...regFacility("name")}
              />

              <div>
                <Controller
                  name="type"
                  control={facilityControl}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        label={t("onboarding.facility.type")}
                        required
                        value={field.value}
                        onChange={field.onChange}
                        options={[
                          {
                            label: t("onboarding.facility.types.hospital"),
                            value: "HOSPITAL",
                          },
                          {
                            label: t("onboarding.facility.types.clinic"),
                            value: "CLINIC",
                          },
                          {
                            label: t("onboarding.facility.types.nursing_home"),
                            value: "NURSING_HOME",
                          },
                          {
                            label: t(
                              "onboarding.facility.types.radiology_center"
                            ),
                            value: "RADIOLOGY_CENTER",
                          },
                          {
                            label: t("onboarding.facility.types.health_centre"),
                            value: "HEALTH_CENTRE",
                          },
                          {
                            label: t(
                              "onboarding.facility.types.emergency_services"
                            ),
                            value: "EMERGENCY_SERVICES",
                          },
                          {
                            label: t(
                              "onboarding.facility.types.physiotherapy_center"
                            ),
                            value: "PHYSIOTHERAPY_CENTER",
                          },
                          {
                            label: t("onboarding.facility.types.health_post"),
                            value: "HEALTH_POST",
                          },
                        ]}
                      />
                      {fieldState.error && (
                        <p className="mt-1 text-sm text-red-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              <Input
                type="email"
                label={t("onboarding.facility.email")}
                required
                placeholder={t("onboarding.facility.emailPlaceholder")}
                leftIcon={<Mail className="w-4 h-4" />}
                error={facilityErrors.email?.message}
                {...regFacility("email")}
              />

              <Controller
                name="phone"
                control={facilityControl}
                render={({ field, fieldState }) => (
                  <PhoneInput
                    label={t("onboarding.facility.phone")}
                    required
                    {...field}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <div className="md:col-span-2">
                <Input
                  type="text"
                  label={t("onboarding.facility.address")}
                  required
                  placeholder={t("onboarding.facility.addressPlaceholder")}
                  leftIcon={<MapPin className="w-4 h-4" />}
                  error={facilityErrors.address?.message}
                  {...regFacility("address")}
                />
              </div>

              <Input
                type="text"
                label={t("onboarding.facility.city")}
                required
                placeholder={t("onboarding.facility.cityPlaceholder")}
                leftIcon={<MapPin className="w-4 h-4" />}
                error={facilityErrors.city?.message}
                {...regFacility("city")}
              />

              <div>
                <Controller
                  name="state"
                  control={facilityControl}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        label={t("onboarding.facility.state")}
                        required
                        value={field.value}
                        onChange={field.onChange}
                        options={stateOptions}
                        placeholder={t("onboarding.facility.statePlaceholder")}
                      />
                      {fieldState.error && (
                        <p className="mt-1 text-sm text-red-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              <Input
                type="text"
                label={t("onboarding.facility.zip")}
                placeholder={t("onboarding.facility.zipPlaceholder")}
                leftIcon={<MapPin className="w-4 h-4" />}
                error={facilityErrors.zip?.message}
                {...regFacility("zip")}
              />

              <div className="md:col-span-2">
                <Input
                  type="text"
                  label={t("onboarding.facility.accessInformation")}
                  placeholder={t(
                    "onboarding.facility.accessInformationPlaceholder"
                  )}
                  {...regFacility("accessInformation")}
                />
              </div>

              {/* MED-1458: no silent https:// rewrite on blur. It turned "not a
                  website" into "https://notawebsite", which looked accepted and
                  only failed as an opaque 400 on the last step. The field is
                  validated against the backend's own URL rule instead. */}
              <Input
                type="url"
                label={t("onboarding.facility.website")}
                placeholder={t("onboarding.facility.websitePlaceholder")}
                leftIcon={<Globe className="w-4 h-4" />}
                error={facilityErrors.website?.message}
                {...regFacility("website")}
              />

              <div className="md:col-span-2">
                <SpecialtySearchAutocomplete
                  specialties={specialtiesForWidget}
                  selectedSpecialties={selectedSpecialties}
                  onSpecialtiesChange={setSelectedSpecialties}
                  title={t("onboarding.facility.specialtiesTitle")}
                  label={t("onboarding.facility.specialtiesLabel")}
                  placeholder={t("onboarding.facility.specialtiesPlaceholder")}
                  getSelectedCountText={count =>
                    t("onboarding.facility.specialtiesSelected", { count })
                  }
                />
              </div>
            </div>
          </div>
        );
      }

      case 2: {
        const {
          register: regManager,
          control: managerControl,
          formState: { errors: managerErrors },
          setValue: setManagerValue,
        } = managerForm;

        const handleSelectManager = (m: Manager) => {
          setManagerValue("civility", m.civility ?? "");
          setManagerValue("firstName", m.firstName);
          setManagerValue("lastName", m.lastName);
          setManagerValue("email", m.email);
          setManagerValue("phone", m.phoneNumber ?? "");
          setOnboardingData(prev => ({
            ...prev,
            selectedManagerId: m.id,
            manager: {
              civility: m.civility ?? "",
              firstName: m.firstName,
              lastName: m.lastName,
              email: m.email,
              phone: m.phoneNumber ?? "",
              dateOfBirth: instantToDateInput(m.dateOfBirth),
            },
          }));
          setManagerMode("form");
        };

        if (managerMode === "search") {
          return (
            <div className="space-y-4">
              {/* Search input */}
              <div className="relative">
                <div className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={managerSearchQuery}
                  onChange={e => setManagerSearchQuery(e.target.value)}
                  placeholder={t("onboarding.manager.searchPlaceholder")}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Loading */}
              {managerSearchLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t("onboarding.facility.searching")}</span>
                </div>
              )}

              {/* Search error */}
              {managerSearchError && !managerSearchLoading && (
                <p className="text-sm text-red-600">{managerSearchError}</p>
              )}

              {/* Results */}
              {!managerSearchLoading &&
                !managerSearchError &&
                managerSearchResults !== null && (
                  <div>
                    {managerSearchResults.length === 0 ? (
                      <div className="p-4 space-y-3 text-center border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {t("onboarding.manager.noManagerFound")}
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            managerForm.reset();
                            setOnboardingData(prev => ({
                              ...prev,
                              selectedManagerId: null,
                            }));
                            setManagerMode("form");
                          }}
                        >
                          {t("onboarding.manager.addNewManager")}
                        </Button>
                      </div>
                    ) : (
                      <ul className="overflow-hidden border border-gray-200 divide-y divide-gray-100 rounded-lg">
                        {managerSearchResults.map(m => (
                          <li
                            key={m.id}
                            className="flex items-start gap-3 p-4 transition-colors cursor-pointer hover:bg-blue-50"
                            onClick={() => handleSelectManager(m)}
                          >
                            <User className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {m.firstName} {m.lastName}
                              </p>
                              <p className="mt-0.5 truncate text-sm text-gray-500">
                                {m.email}
                              </p>
                              <p className="mt-0.5 truncate text-xs text-gray-400">
                                {m.healthcareFacility?.name ??
                                  t("onboarding.manager.unassigned")}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

              {/* Hint when no search yet */}
              {!managerSearchLoading &&
                managerSearchResults === null &&
                !managerSearchError && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      {t("onboarding.manager.searchHint")}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        managerForm.reset();
                        setOnboardingData(prev => ({
                          ...prev,
                          selectedManagerId: null,
                        }));
                        setManagerMode("form");
                      }}
                    >
                      {t("onboarding.manager.addNewManager")}
                    </Button>
                  </div>
                )}
            </div>
          );
        }

        // managerMode === 'form'
        return (
          <div className="space-y-6">
            {/* Back to search */}
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
              onClick={() => {
                setManagerMode("search");
                setOnboardingData(prev => ({
                  ...prev,
                  selectedManagerId: null,
                }));
                setManagerSearchResults(null);
                setManagerSearchQuery("");
                setManagerAlreadyHasFacility(false);
              }}
            >
              {t("onboarding.manager.backToSearch")}
            </button>

            {/* Existing manager banner */}
            {onboardingData.selectedManagerId !== null && (
              <div className="px-4 py-3 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50">
                {t("onboarding.manager.existingManagerBanner")}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* MED-1543: onboarding never asked, and posted "M." for
                  everyone. Same control and the same two labels the other
                  manager forms use. */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="onboarding-manager-civility"
                  className="text-sm font-medium text-gray-700"
                >
                  {t("onboarding.manager.civility")}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <Controller
                  name="civility"
                  control={managerControl}
                  render={({ field }) => (
                    <select
                      {...field}
                      id="onboarding-manager-civility"
                      className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">
                        {t("onboarding.manager.civility")}
                      </option>
                      <option value={CivilityEnum.MR}>{t("common.mr")}</option>
                      <option value={CivilityEnum.MRS}>
                        {t("common.mrs")}
                      </option>
                    </select>
                  )}
                />
                {managerErrors.civility && (
                  <p className="mt-1 text-sm text-red-600">
                    {managerErrors.civility.message}
                  </p>
                )}
              </div>

              <Input
                type="text"
                label={t("onboarding.manager.firstName")}
                required
                placeholder={t("onboarding.manager.firstNamePlaceholder")}
                leftIcon={<User className="w-4 h-4" />}
                error={managerErrors.firstName?.message}
                {...regManager("firstName")}
              />

              <Input
                type="text"
                label={t("onboarding.manager.lastName")}
                required
                placeholder={t("onboarding.manager.lastNamePlaceholder")}
                leftIcon={<User className="w-4 h-4" />}
                error={managerErrors.lastName?.message}
                {...regManager("lastName")}
              />

              <Input
                type="email"
                label={t("onboarding.manager.email")}
                required
                placeholder={t("onboarding.manager.emailPlaceholder")}
                leftIcon={<Mail className="w-4 h-4" />}
                error={managerErrors.email?.message}
                {...regManager("email")}
              />

              <Controller
                name="phone"
                control={managerControl}
                render={({ field, fieldState }) => (
                  <PhoneInput
                    label={t("onboarding.manager.phone")}
                    required
                    {...field}
                    error={fieldState.error?.message}
                  />
                )}
              />

              {/* MED-1543: the field showed an ISO-looking "yyyy-mm-dd" hint
                  on a French UI. A native date input renders its own
                  placeholder and the `placeholder` attribute does nothing to
                  it; `lang` is the only hook the platform offers, and whether
                  the browser honours it for the displayed format is up to the
                  browser. It is correct to declare either way — assistive
                  technology reads the date in this language. */}
              <Input
                type="date"
                lang={i18n.language}
                label={t("onboarding.manager.dateOfBirth")}
                error={managerErrors.dateOfBirth?.message}
                {...regManager("dateOfBirth")}
              />
            </div>
          </div>
        );
      }

      case 3: {
        const {
          register: regPromotion,
          setValue: setPromotionValue,
          watch: watchPromotion,
        } = promotionForm;
        const typedPromotionCode = watchPromotion("promotionCode") ?? "";
        const selectedPromotion = resolvePromotionCode(typedPromotionCode);
        // MED-1487: the code is typed, so say straight away that what was
        // typed is not a promotion — the dates below would otherwise just never
        // appear, with nothing said about why. Derived, not read from the form
        // state: a submit-time error on this field can only ever be this same
        // message, and reading it live also means it clears the moment the code
        // does resolve.
        const unknownCode =
          typedPromotionCode.trim() !== "" && selectedPromotion === undefined;

        // MED-1539: labels follow the UI language.
        const promotionOptions = [
          { value: "", label: t("onboarding.promotion.noneOption") },
          ...promotions.map(p => ({
            value: p.promotionCode,
            label: `${promotionLabel(p)} (-${p.rebate ?? 0}%)`,
          })),
        ];

        return (
          <div className="space-y-6">
            <div className="p-6 rounded-lg bg-gray-50">
              <h3 className="mb-1 text-lg font-semibold text-gray-900">
                {t("onboarding.steps.promotion.title")}
              </h3>
              <p className="mb-4 text-sm text-gray-600">
                {t("onboarding.steps.promotion.description")}
              </p>

              {promotionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-b-2 border-blue-600 rounded-full animate-spin" />
                </div>
              ) : promotions.length === 0 ? (
                /* MED-1487: there is no promotion to apply at all, so there is
                   nothing to type or pick either. Say where promotions are
                   authored instead of showing an empty field. */
                <p className="text-sm text-gray-600">
                  {t("onboarding.promotion.noneAvailable")}
                </p>
              ) : (
                <div className="space-y-4">
                  {/* MED-1487: a code can be typed in directly. It is only
                      applied, never created, here — POST
                      /onboarding/promotions/apply needs a code that already
                      exists, and codes with their rebate are authored in
                      Licences → Promotions. */}
                  <Input
                    label={t("onboarding.promotion.codeLabel")}
                    placeholder={t("onboarding.promotion.codePlaceholder")}
                    error={
                      unknownCode
                        ? t("onboarding.promotion.codeNotFound")
                        : undefined
                    }
                    {...regPromotion("promotionCode")}
                  />

                  <Select
                    label={t("onboarding.promotion.pickerLabel")}
                    value={selectedPromotion?.promotionCode ?? ""}
                    onChange={value =>
                      setPromotionValue("promotionCode", String(value ?? ""), {
                        shouldDirty: true,
                      })
                    }
                    options={promotionOptions}
                    placeholder={t("onboarding.promotion.selectPlaceholder")}
                  />

                  {selectedPromotion && (
                    <>
                      {selectedPromotion.rebate != null && (
                        <p className="text-sm font-medium text-green-600">
                          {t("onboarding.promotion.rebateLabel", {
                            rebate: selectedPromotion.rebate,
                          })}
                        </p>
                      )}

                      {/* MED-1539: the period is the promotion's own, authored
                          in Licences -> Promotions. It used to be two empty date
                          inputs, and the wizard sent them to an endpoint that
                          creates a second period from them — so the promotion
                          being picked was left with no facility at all. */}
                      <div className="text-sm text-gray-700">
                        <p className="font-medium">
                          {t("onboarding.promotion.validityPeriod")}
                        </p>
                        <p>
                          {formatDateTime(selectedPromotion.startDate)}
                          {" → "}
                          {formatDateTime(selectedPromotion.endDate)}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {t("onboarding.promotion.periodNote")}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      case 4:
        return (
          <div className="space-y-6">
            <div className="p-6 rounded-lg bg-gray-50">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                {t("onboarding.summary.title")}
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t("onboarding.summary.license")}
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>Plan:</strong>{" "}
                      {onboardingData.license &&
                        t(
                          `licenses.plans.${onboardingData.license.type.toLowerCase().replace("standard", "professional")}`
                        )}
                    </p>
                    <p>
                      <strong>
                        {t("onboarding.steps.doctorCount.title")}:
                      </strong>{" "}
                      {onboardingData.doctorCount}{" "}
                      {t("onboarding.steps.doctorCount.doctors", {
                        count: onboardingData.doctorCount,
                      })}
                    </p>
                    {onboardingData.isTrial ? (
                      <p>
                        <strong>{t("facilityLicence.startTrialLabel")}:</strong>{" "}
                        {t(
                          `onboarding.steps.trialDuration.${onboardingData.trialDurationMonths}_MONTH${onboardingData.trialDurationMonths > 1 ? "S" : ""}`
                        )}
                      </p>
                    ) : (
                      onboardingData.contractDuration && (
                        <p>
                          <strong>
                            {t("onboarding.steps.contractDuration.title")}:
                          </strong>{" "}
                          {t(
                            `onboarding.steps.contractDuration.${onboardingData.contractDuration}`
                          )}
                        </p>
                      )
                    )}
                    {onboardingData.license && !onboardingData.isTrial && (
                      <div>
                        <p>
                          <strong>
                            {t("onboarding.steps.doctorCount.totalPrice")}:
                          </strong>{" "}
                          {formatMoney(
                            calculateTotalPrice(
                              onboardingData.license,
                              onboardingData.doctorCount,
                              onboardingData.contractDuration
                            )
                          )}
                          /{t("licenses.month")}
                        </p>
                        {getDiscountRate(onboardingData.contractDuration) >
                          0 && (
                          <p className="text-green-600">
                            <strong>
                              {t("onboarding.steps.contractDuration.discount")}:
                            </strong>{" "}
                            -{getDiscountRate(onboardingData.contractDuration)}%
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">
                    {t("onboarding.summary.facility")}
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>{t("onboarding.facility.name")}:</strong>{" "}
                      {onboardingData.facility.name}
                    </p>
                    <p>
                      <strong>{t("onboarding.facility.type")}:</strong>{" "}
                      {t(
                        `onboarding.facility.types.${onboardingData.facility.type.toLowerCase()}`
                      )}
                    </p>
                    <p>
                      <strong>{t("onboarding.facility.email")}:</strong>{" "}
                      {onboardingData.facility.email}
                    </p>
                    <p>
                      <strong>{t("onboarding.facility.phone")}:</strong>{" "}
                      {onboardingData.facility.phone}
                    </p>
                    <p>
                      <strong>{t("onboarding.facility.address")}:</strong>{" "}
                      {onboardingData.facility.address},{" "}
                      {onboardingData.facility.city}
                    </p>
                    {/* MED-1543: the recap asked for both of these on the
                        facility step and then never showed them back, so there
                        was no way to check them before submitting. The website
                        is optional, so it is only listed when there is one. */}
                    <p>
                      <strong>{t("onboarding.facility.state")}:</strong>{" "}
                      {onboardingData.facility.state}
                    </p>
                    {onboardingData.facility.website && (
                      <p>
                        <strong>{t("onboarding.facility.website")}:</strong>{" "}
                        {onboardingData.facility.website}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">
                    {t("onboarding.summary.manager")}
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>{t("onboarding.manager.firstName")}:</strong>{" "}
                      {onboardingData.manager.firstName}
                    </p>
                    <p>
                      <strong>{t("onboarding.manager.lastName")}:</strong>{" "}
                      {onboardingData.manager.lastName}
                    </p>
                    <p>
                      <strong>{t("onboarding.manager.email")}:</strong>{" "}
                      {onboardingData.manager.email}
                    </p>
                    <p>
                      <strong>{t("onboarding.manager.phone")}:</strong>{" "}
                      {onboardingData.manager.phone}
                    </p>
                  </div>
                </div>

                {onboardingData.promotion && (
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {t("onboarding.summary.promotion")}
                    </h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>
                        <strong>{t("onboarding.promotion.codeLabel")}:</strong>{" "}
                        {onboardingData.promotion.promotionCode ===
                        FREE_TRIAL_PROMOTION_CODE
                          ? t("onboarding.promotion.freeTrialLabel")
                          : onboardingData.promotion.promotionCode}
                      </p>
                      <p>
                        <strong>{t("onboarding.promotion.startDate")}:</strong>{" "}
                        {formatDateTime(onboardingData.promotion.startDate)}
                      </p>
                      <p>
                        <strong>{t("onboarding.promotion.endDate")}:</strong>{" "}
                        {formatDateTime(onboardingData.promotion.endDate)}
                      </p>
                      {/* MED-1539: the rebate never reached this recap, which
                          quoted the undiscounted monthly price. medecine360-api
                          applies the promotion to the facility's invoices by
                          their due date, and reprices the facility's unpaid
                          invoices when the promotion is assigned; onboarding
                          assigns it right after creating the licence. So the
                          discounted figure is what the facility is billed for
                          invoices falling due within the period above. */}
                      {onboardingData.promotion.rebate != null &&
                        onboardingData.license &&
                        !onboardingData.isTrial && (
                          <>
                            <p className="text-green-600">
                              <strong>
                                {t("onboarding.promotion.rebateLabel", {
                                  rebate: onboardingData.promotion.rebate,
                                })}
                              </strong>
                            </p>
                            <p>
                              <strong>
                                {t("onboarding.promotion.discountedPrice")}:
                              </strong>{" "}
                              {formatMoney(
                                priceWithRebate(
                                  calculateTotalPrice(
                                    onboardingData.license,
                                    onboardingData.doctorCount,
                                    onboardingData.contractDuration
                                  ),
                                  onboardingData.promotion.rebate
                                )
                              )}
                              /{t("licenses.month")}
                            </p>
                          </>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div ref={pageTopRef} />
      {/* Header */}
      <div className="page-header">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("onboarding.title")}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{t("onboarding.subtitle")}</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t("common.error")}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                    onClick={() => setError(null)}
                  >
                    {t("common.dismiss")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Steps */}
      <div className="page-section card-container">
        <Steps
          steps={steps}
          currentStep={currentStep + 1}
          completedSteps={completedSteps}
        />
      </div>

      {/* Step Content */}
      <div key={currentStep} className="page-section card-container">
        {renderStepContent()}
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <div>
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0 || isLoading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.previous")}
          </Button>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={
                isLoading ||
                (currentStep === 0 && !isStep0Valid()) ||
                (currentStep === 1 && facilityMode === "search") ||
                (currentStep === 1 && facilityAlreadyAssigned) ||
                (currentStep === 2 && managerMode === "search") ||
                (currentStep === 2 && managerAlreadyHasFacility)
              }
            >
              {t("common.next")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCompleteOnboarding}
              disabled={isCompleting || isLoading}
            >
              {isCompleting
                ? t("onboarding.completing")
                : t("onboarding.complete")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
